<script setup lang="ts">
    // @ts-expect-error raison: lib externe mal typée
    import { reactive, ref, watch, defineProps, defineEmits, defineExpose } from 'vue';
    import axios from 'axios';
    import moment from 'moment';
    import { AMOUNT_FORMAT_CONFIG, PERCENTAGE_FORMAT_CONFIG } from '@/utils';

    const props = defineProps({
        collectivites: { type: Object, required: true },
        hideCollectiviteField: { type: Boolean, default: false },
        hideSubmitButton: { type: Boolean, default: false },
        hideEngagementFields: { type: Boolean, default: false },
        fullWidth: { type: Boolean, default: false },
    });
    const emit = defineEmits(['saved']);
    const errorMessage = ref('');
    const loading = ref(false);
    const isFormValid = ref(false);
    const formData = reactive({
        nom: null as string | null,
        prenom: null as string | null,
        date_naissance: null as string | null,
        age: 0,
        date_engagement: null as string | null,
        anciennete: 0,
        collectivite_id: null as number | null,
        salaire: "",
        quotite_cessible_pourcent: "",
        quotite_cessible_montant: "",
        telephone: null as string | null,
        email: null as string | null,
    });
    const errors = reactive({
        nom: "",
        prenom: "",
        date_naissance: "",
        age: "",
        date_engagement: "",
        anciennete: "",
        collectivite_id: "",
        salaire: "",
        quotite_cessible_pourcent: "",
        quotite_cessible_montant: "",
        telephone: "",
        email: "",
        pays_id: ""
    });
    const countries = [
        { name: 'États-Unis', code: 'US', dial: '+1' },
        { name: 'Canada', code: 'CA', dial: '+1' },
        { name: 'Sénégal', code: 'SN', dial: '+221' },
        { name: 'Mali', code: 'ML', dial: '+223' },
        { name: 'Guinée', code: 'GN', dial: '+224' },
        { name: 'Côte d’Ivoire', code: 'CI', dial: '+225' },
        { name: 'Burkina Faso', code: 'BF', dial: '+226' },
        { name: 'Niger', code: 'NE', dial: '+227' },
        { name: 'Togo', code: 'TG', dial: '+228' },
        { name: 'Bénin', code: 'BJ', dial: '+229' },
        { name: 'Ghana', code: 'GH', dial: '+233' },
        { name: 'Nigeria', code: 'NG', dial: '+234' },
        { name: 'Rwanda', code: 'RW', dial: '+250' },
        { name: 'Éthiopie', code: 'ET', dial: '+251' },
        { name: 'Kenya', code: 'KE', dial: '+254' },
        { name: 'Tanzanie', code: 'TZ', dial: '+255' },
        { name: 'Afrique du Sud', code: 'ZA', dial: '+27' },
        { name: 'Belgique', code: 'BE', dial: '+32' },
        { name: 'France', code: 'FR', dial: '+33' },
        { name: 'Luxembourg', code: 'LU', dial: '+352' },
        { name: 'Suisse', code: 'CH', dial: '+41' },
        { name: 'Allemagne', code: 'DE', dial: '+49' },
        { name: 'Mexique', code: 'MX', dial: '+52' },
        { name: 'Brésil', code: 'BR', dial: '+55' },
        { name: 'Japon', code: 'JP', dial: '+81' },
        { name: 'Chine', code: 'CN', dial: '+86' },
        { name: 'Inde', code: 'IN', dial: '+91' },
        { name: 'Émirats Arabes Unis', code: 'AE', dial: '+971' },
    ];

    function recomputeAge() {
        if (!formData.date_naissance) { formData.age = 0; return; }
        const birthDate = moment(formData.date_naissance);
        if (birthDate.isValid()) formData.age = moment().diff(birthDate, 'years');
    }

    function recomputeAnciennete() {
        if (!formData.date_engagement) { formData.anciennete = 0; return; }
        const engagementDate = moment(formData.date_engagement);
        if (engagementDate.isValid()) formData.anciennete = moment().diff(engagementDate, 'years');
    }

    function onDateNaissanceChange(e: Event) {
        formData.date_naissance = (e.target as HTMLInputElement).value;
        recomputeAge();
    }

    function onDateEngagementChange(e: Event) {
        formData.date_engagement = (e.target as HTMLInputElement).value;
        recomputeAnciennete();
    }

    // Fallback : watcher pour binding programmatique
    watch(() => formData.date_naissance, () => recomputeAge());
    watch(() => formData.date_engagement, () => recomputeAnciennete());

    function validate() {
        let isValid = true;

        errors.nom = '';
        if (!formData.nom) {
            errors.nom ="Le nom est requis";
            isValid = false;
        }

        errors.prenom = '';
        if (!formData.prenom) {
            errors.prenom ="Le prénom est requis";
            isValid = false;
        }

        errors.date_naissance = '';
        if (!formData.date_naissance) {
            errors.date_naissance ="La date de naissance est requise";
            isValid = false;
        } else {
            const birthDate = moment(formData.date_naissance);
            if (!birthDate.isValid()) {
                errors.date_naissance ="Le format de la date de naissance est invalide";
                isValid = false;
            }
        }

        errors.age = '';
        if (!formData.age) {
            errors.age ="L'âge est requis";
            isValid = false;
        } else if (isNaN(formData.age) || formData.age <= 0) {
            errors.age ="Le format de l'âge est invalide";
            isValid = false;
        }

        errors.date_engagement = '';
        errors.anciennete = '';
        if (!props.hideEngagementFields) {
            if (!formData.date_engagement) {
                errors.date_engagement ="La date d'engagement est requise";
                isValid = false;
            } else {
                const engagementDate = moment(formData.date_engagement);
                if (!engagementDate.isValid()) {
                    errors.date_engagement ="Le format de la date d'engagement est invalide";
                    isValid = false;
                }
            }

            if (!formData.anciennete) {
                errors.anciennete ="L'ancienneté est requise";
                isValid = false;
            } else if (isNaN(formData.anciennete) || formData.anciennete < 0) {
                errors.anciennete ="Le format de l'ancienneté est invalide";
                isValid = false;
            }
        }

        errors.collectivite_id = '';
        if (!props.hideCollectiviteField) {
            if (!formData.collectivite_id) {
                errors.collectivite_id ="La collectivité est requise";
                isValid = false;
            } else if (isNaN(formData.collectivite_id) || formData.collectivite_id <= 0) {
                errors.collectivite_id ="Le format de la collectivité est invalide";
                isValid = false;
            }
        }

        errors.salaire = '';
        errors.quotite_cessible_pourcent = '';
        errors.quotite_cessible_montant = '';
        if (!props.hideEngagementFields) {
            if (!formData.salaire) {
                errors.salaire ="Le salaire est requis";
                isValid = false;
            } else if (isNaN(parseFloat(formData.salaire)) || parseFloat(formData.salaire) <= 0) {
                errors.salaire ="Le format du salaire est invalide";
                isValid = false;
            }

            if (!formData.quotite_cessible_pourcent) {
                errors.quotite_cessible_pourcent ="La quotité cessible en pourcentage est requise";
                isValid = false;
            } else if (isNaN(parseFloat(formData.quotite_cessible_pourcent)) || parseFloat(formData.quotite_cessible_pourcent) <= 0 || parseFloat(formData.quotite_cessible_pourcent) > 100) {
                errors.quotite_cessible_pourcent ="Le format de la quotité cessible en pourcentage est invalide";
                isValid = false;
            }

            if (!formData.quotite_cessible_montant) {
                errors.quotite_cessible_montant ="La quotité cessible en montant est requise";
                isValid = false;
            } else if (isNaN(parseFloat(formData.quotite_cessible_montant)) || parseFloat(formData.quotite_cessible_montant) <= 0) {
                errors.quotite_cessible_montant ="Le format de la quotité cessible en montant est invalide";
                isValid = false;
            }
        }

        errors.telephone = '';
        if (!formData.telephone) {
            errors.telephone ="Le téléphone est requis";
            isValid = false;
        }

        errors.email = '';
        if (!formData.email) {
            errors.email ="L'email est requis";
            isValid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = "Le format de l'email est invalide";
            isValid = false;
        }

        isFormValid.value = isValid;
        
        return isValid;
    }

    async function onSubmit() {
        if (!validate()) { return; }
        const formToUpload = {
            nom: formData.nom,
            prenom: formData.prenom,
            date_naissance: formData.date_naissance,
            age: formData.age,
            date_engagement: formData.date_engagement,
            anciennete: formData.anciennete,
            collectivite_id: formData.collectivite_id,
            salaire: formData.salaire,
            quotite_cessible_pourcent: formData.quotite_cessible_pourcent,
            quotite_cessible_montant: formData.quotite_cessible_montant,
            telephone: formData.telephone,
            email: formData.email,
        };
        emit('saved', formToUpload);
    }

    async function doCalculateQuotiteCessible() {
        const salaire = parseFloat(formData.salaire);
        const quotite_cessible_pourcent = parseFloat(formData.quotite_cessible_pourcent);
        if (!salaire || isNaN(salaire) || !quotite_cessible_pourcent || isNaN(quotite_cessible_pourcent)) {
            return;
        }

        loading.value = true;
        errorMessage.value = '';
        try {
            const response = await axios.post(`/backoffice/onboarding/calculate_quotite_cessible`, {
                salaire: formData.salaire,
                quotite_cessible_pourcent: formData.quotite_cessible_pourcent,
            });
            formData.quotite_cessible_montant = response.data.quotite_cessible_montant;
        } catch(error: any) {
            errorMessage.value = error.message;
            console.error(error);
        } finally {
            loading.value = false;
        }
    }

    // Exposer les données et fonctions pour usage externe
    defineExpose({
        formData,
        validate,
        onSubmit
    });
</script>

<style scoped>
.form-field-with-prefix {
    padding-left: 100px;
    position: relative;
}
.form-input-prefix {
    position: absolute;
    left: 0;
    width: 100px;
    height: 100%;
    font-size: 1rem;
    line-height: 1rem;
    color: var(--component-text);
    font-weight: 600;
    background-color: transparent;
    border: none;
    outline: none;
    padding-left: 15px;
}
</style>

<template>
    <form :class="fullWidth ? 'form' : 's20 form'">

        <div :class="fullWidth ? '' : 'container-full-1'">
            <h3 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                <img src="/icons/icon-info-perso.svg" class="size-base" alt="" />
                Informations personnelles
            </h3>

            <div class="form-field-floating reactive active my-5">
                <input type="text" class="form-input" v-model="formData.nom" name="nom" id="nom" placeholder="">
                <label for="nom">Nom <b class="color-danger">*</b></label>
            </div>
            <span v-if="!!errors['nom']" class="text-red">{{ errors['nom'] }}</span>
            
            <div class="form-field-floating reactive my-5">
                <input type="text" class="form-input" v-model="formData.prenom" name="prenom" id="prenom" placeholder="">
                <label for="prenom">Prénoms <b class="color-danger">*</b></label>
            </div>
            <span v-if="!!errors['prenom']" class="text-red">{{ errors['prenom'] }}</span>

            <div class="form-field-split my-5">
                <div class="form-field-floating reactive half">
                    <input type="date" class="form-input" :value="formData.date_naissance" @input="onDateNaissanceChange" @change="onDateNaissanceChange" name="date_naissance" id="date_naissance" placeholder="">
                    <label for="date_naissance">Date de naissance <b class="color-danger">*</b></label>
                </div>
                <hr class="hr" />
                <div class="form-field-floating reactive half">
                    <input type="text" class="form-input" v-model="formData.age" name="age" id="age" placeholder="" readonly>
                    <label for="age">Age <b class="color-danger">*</b></label>
                </div>
            </div>
            <span v-if="!!errors['date_naissance']" class="text-red">{{ errors['date_naissance'] }}</span>
            <span v-if="!!errors['age']" class="text-red">{{ errors['age'] }}</span>

            <div v-if="!hideEngagementFields" class="form-field-split my-5">
                <div class="form-field-floating reactive half">
                    <input type="date" class="form-input" :value="formData.date_engagement" @input="onDateEngagementChange" @change="onDateEngagementChange" name="date_engagement" id="date_engagement" placeholder="">
                    <label for="date_engagement">Date d'engagement <b class="color-danger">*</b></label>
                </div>
                <hr class="hr" />
                <div class="form-field-floating reactive half">
                    <input type="text" class="form-input" v-model="formData.anciennete" name="anciennete" id="anciennete" placeholder="">
                    <label for="anciennete">Ancienneté <b class="color-danger">*</b></label>
                </div>
            </div>
            <span v-if="!hideEngagementFields && !!errors['date_engagement']" class="text-red">{{ errors['date_engagement'] }}</span>
            <span v-if="!hideEngagementFields && !!errors['anciennete']" class="text-red">{{ errors['anciennete'] }}</span>

            <div v-if="!hideCollectiviteField" class="form-field-floating full my-5">
                <select class="form-input-select" v-model="formData.collectivite_id" name="collectivite_id" id="collectivite_id">
                    <option :value="null">Sélectionnez votre collectivité</option>
                    <option :value="collectivite.id" v-for="(collectivite, index) in props.collectivites" :key="'collectivite_' + index">
                        {{ collectivite.nom }}
                    </option>
                </select>
                <label for="collectivite_id">Votre collectivité <b class="color-danger">*</b></label>
            </div>
            <span v-if="!hideCollectiviteField && !!errors['collectivite_id']" class="text-red">{{ errors['collectivite_id'] }}</span>
        </div>

        <div class="clear-both h-5"></div>

        <div :class="fullWidth ? '' : 'container-full-1'">
            <template v-if="!hideEngagementFields">
                <h3 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                    <img src="/icons/icon-engagement.svg" class="size-base" alt="" />
                    Engagement
                </h3>
                <div class="form-field-floating reactive active my-5">
                    <money3 class="form-input" v-model="formData.salaire" name="salaire" id="salaire" placeholder="" v-bind="AMOUNT_FORMAT_CONFIG" @blur="doCalculateQuotiteCessible"></money3>
                    <label for="salaire">Salaire brut <b class="color-danger">*</b></label>
                    <span>F&nbsp;CFA</span>
                </div>
                <span v-if="!!errors['salaire']" class="text-red">{{ errors['salaire'] }}</span>

                <div class="form-field-split active my-5">
                    <div class="form-field-floating reactive half">
                        <money3 class="form-input" v-model="formData.quotite_cessible_pourcent" name="quotite_cessible_pourcent" id="quotite_cessible_pourcent" placeholder="" v-bind="PERCENTAGE_FORMAT_CONFIG" @blur="doCalculateQuotiteCessible"></money3>
                        <label for="quotite_cessible_pourcent">Quotité cessible max <b class="color-danger">*</b></label>
                        <span class="">%</span>
                    </div>
                    <hr class="hr" />
                    <div class="form-field-floating reactive half">
                        <money3 class="form-input" v-model="formData.quotite_cessible_montant" name="quotite_cessible_montant" id="quotite_cessible_montant" placeholder="" v-bind="AMOUNT_FORMAT_CONFIG" readonly></money3>
                        <label for="quotite_cessible_montant">Valeur <b class="color-danger">*</b></label>
                        <span class="">F&nbsp;CFA</span>
                    </div>
                </div>
                <span v-if="!!errors['quotite_cessible_pourcent']" class="text-red">{{ errors['quotite_cessible_pourcent'] }}</span>
                <span v-if="!!errors['quotite_cessible_montant']" class="text-red">{{ errors['quotite_cessible_montant'] }}</span>
            </template>

            <div class="form-field-split active my-5">
                <custom-phone-input v-model="formData.telephone" id="telephone" classesNames="half" />
                <hr class="hr" />
                <div class="form-field-floating reactive half">
                    <input type="text" class="form-input" v-model="formData.email" name="email" id="email" placeholder="">
                    <label for="email">Adresse Email <b class="color-danger">*</b></label>
                </div>
            </div>
            <span v-if="!!errors['email']" class="text-red">{{ errors['email'] }}</span>

            <button v-if="!hideSubmitButton" type="button" id="btn_create_account" class="btn flex-center mt-8 mb-7" @click="onSubmit()">
                <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                <span>Créer le compte</span>
            </button>

            <!-- <button class="btn lg w-100 flex-center mt-5 mb-6">
                            <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                            <span>Valider</span>
                        </button> -->
        </div>

    </form>
</template>
